import * as React from "react"
import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog"
import { Button } from "@/primitives/button"
import { cn } from "@/primitives/utils"

function AlertDialog({ ...props }: AlertDialogPrimitive.Root.Props) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />
}

function AlertDialogTrigger({ ...props }: AlertDialogPrimitive.Trigger.Props) {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
}

function AlertDialogPortal({ ...props }: AlertDialogPrimitive.Portal.Props) {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
}

function AlertDialogOverlay({ className, ...props }: AlertDialogPrimitive.Backdrop.Props) {
  return (
    <AlertDialogPrimitive.Backdrop
      data-slot="alert-dialog-overlay"
      className={cn(
        // beacon: the scrim token, with no blur, as Dialog's.
        "fixed inset-0 isolate z-50 bg-scrim duration-150 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0",
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogContent({
  className,
  size = "default",
  initialFocus,
  ...props
}: AlertDialogPrimitive.Popup.Props & {
  size?: "default" | "sm"
}) {
  const popup = React.useRef<HTMLDivElement>(null)
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Popup
        ref={popup}
        data-slot="alert-dialog-content"
        data-size={size}
        // beacon: the confirming action takes focus as the dialog opens, so Enter confirms; the
        // choice is on screen and Esc still cancels.
        initialFocus={
          initialFocus ??
          (() =>
            popup.current?.querySelector<HTMLElement>(
              "[data-slot=alert-dialog-action]:not(:disabled)",
            ) ?? true)
        }
        className={cn(
          // beacon: Dialog's surface and entrance, 20px padding and 16px between its parts.
          "group/alert-dialog-content fixed top-1/2 left-1/2 z-50 grid w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-5 text-ui text-popover-foreground shadow-dialog duration-150 ease-snappy outline-none data-[size=default]:max-w-md data-[size=sm]:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-[0.98] data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0 data-closed:zoom-out-[0.98]",
          className,
        )}
        {...props}
      />
    </AlertDialogPortal>
  )
}

function AlertDialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn(
        // beacon: left-aligned; the media tile, when there is one, sits above the title.
        "flex min-w-0 flex-col items-start gap-1 text-left",
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        // beacon: actions at the end, with no rule above them.
        "flex items-center justify-end gap-2",
        className,
      )}
      {...props}
    />
  )
}

const alertDialogMediaTones = {
  destructive: "bg-destructive/10 text-destructive",
  brand: "bg-primary/10 text-brand-text",
} as const

/**
 * beacon: a 36px tile holding the case's icon, tinted by what confirming it does: `destructive`
 * removes something, `brand` grants or archives something consequential.
 */
function AlertDialogMedia({
  className,
  tone = "destructive",
  ...props
}: React.ComponentProps<"div"> & { tone?: keyof typeof alertDialogMediaTones }) {
  return (
    <div
      data-slot="alert-dialog-media"
      data-tone={tone}
      className={cn(
        "mb-3 inline-flex size-9 shrink-0 items-center justify-center rounded-lg *:[svg:not([class*='size-'])]:size-4.5",
        alertDialogMediaTones[tone],
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(
        // beacon: a title in the type scale.
        "font-heading text-title font-semibold",
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn(
        // beacon: UI text in the secondary tier.
        "text-ui text-pretty text-muted-foreground *:[a]:text-brand-text *:[a]:underline-offset-3 *:[a]:hover:underline",
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogAction({ className, ...props }: React.ComponentProps<typeof Button>) {
  return <Button data-slot="alert-dialog-action" className={cn(className)} {...props} />
}

function AlertDialogCancel({
  className,
  variant = "outline",
  size = "default",
  ...props
}: AlertDialogPrimitive.Close.Props &
  Pick<React.ComponentProps<typeof Button>, "variant" | "size">) {
  return (
    <AlertDialogPrimitive.Close
      data-slot="alert-dialog-cancel"
      className={cn(className)}
      render={<Button variant={variant} size={size} />}
      {...props}
    />
  )
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
}
